import React from "react";
import { Bell } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "../../utils/cn";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./dropdown-menu";

export interface NotificationItem {
  id: string;
  /**
   * `NotificationType` from the API. The backend only sends English `title`/`body`, so the bell
   * renders `notifications.types.<type>.*` when the active locale has it and falls back to the
   * server text otherwise.
   */
  type?: string;
  title: string;
  body: string;
  readAt: string | null;
  createdAt: string;
  entityId: string;
  entityType: string;
}

export interface NotificationBellProps {
  notifications?: NotificationItem[];
  isLoading?: boolean;
  onMarkRead: (id: string) => void;
  onNotificationClick?: (item: NotificationItem) => void;
  /** Label for the trigger button (for a11y). */
  label?: string;
  /** Override button class (e.g. for the dark customer navbar). */
  triggerClassName?: string;
}

export function NotificationBell({
  notifications = [],
  isLoading = false,
  onMarkRead,
  onNotificationClick,
  label = "Notifications",
  triggerClassName,
}: NotificationBellProps) {
  const { t } = useTranslation();
  const unreadCount = notifications.filter((n) => !n.readAt).length;
  const unreadLabel = t("notifications.unread", { count: unreadCount });

  function formatRelativeTime(dateStr: string): string {
    const diff = Date.now() - new Date(dateStr).getTime();
    const minutes = Math.floor(diff / 60_000);
    if (minutes < 1) return t("notifications.time.justNow");
    if (minutes < 60) return t("notifications.time.minutesAgo", { count: minutes });
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return t("notifications.time.hoursAgo", { count: hours });
    const days = Math.floor(hours / 24);
    return t("notifications.time.daysAgo", { count: days });
  }

  function localizedCopy(item: NotificationItem) {
    if (!item.type) return { title: item.title, body: item.body };
    return {
      title: t(`notifications.types.${item.type}.title`, { defaultValue: item.title }),
      body: t(`notifications.types.${item.type}.body`, { defaultValue: item.body }),
    };
  }

  function handleItemClick(item: NotificationItem) {
    if (!item.readAt) {
      onMarkRead(item.id);
    }
    onNotificationClick?.(item);
  }

  function renderBody() {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-8">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        </div>
      );
    }
    if (notifications.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center gap-2 px-4 py-10 text-center">
          <Bell className="h-8 w-8 text-surface-on-variant/40" />
          <p className="text-sm text-surface-on-variant">{t("notifications.empty")}</p>
        </div>
      );
    }
    return notifications.map((item, i) => {
      const copy = localizedCopy(item);
      return (
        <React.Fragment key={item.id}>
          {i > 0 && <DropdownMenuSeparator className="my-0" />}
          <DropdownMenuItem
            className={cn(
              "flex cursor-pointer flex-col items-start gap-1 rounded-none px-4 py-3",
              !item.readAt && "bg-primary/5",
            )}
            onClick={() => handleItemClick(item)}
          >
            <div className="flex w-full items-start justify-between gap-2">
              <span
                className={cn(
                  "text-sm leading-snug",
                  !item.readAt ? "font-semibold" : "font-medium",
                )}
              >
                {copy.title}
              </span>
              {!item.readAt && <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-primary" />}
            </div>
            <p className="line-clamp-2 text-xs leading-snug text-surface-on-variant">{copy.body}</p>
            <span className="text-[11px] text-surface-on-variant/60">
              {formatRelativeTime(item.createdAt)}
            </span>
          </DropdownMenuItem>
        </React.Fragment>
      );
    });
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={`${label}${unreadCount > 0 ? ` (${unreadLabel})` : ""}`}
          className={cn(
            "relative flex h-10 w-10 items-center justify-center rounded-full outline-none",
            "border border-outline-variant/60 bg-surface-container-lowest text-surface-on-variant",
            "hover:bg-surface-container-high hover:text-surface-on transition-colors",
            triggerClassName,
          )}
        >
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span className="absolute -end-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-error px-1 text-[10px] font-bold leading-none text-error-on">
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-80 p-0">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-outline-variant/40 px-4 py-3">
          <DropdownMenuLabel className="p-0 text-base font-semibold">{label}</DropdownMenuLabel>
          {unreadCount > 0 && (
            <span className="text-xs text-surface-on-variant">{unreadLabel}</span>
          )}
        </div>

        {/* Body */}
        <div className="max-h-80 overflow-y-auto">{renderBody()}</div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
